/**
 * SanMar "no image" placeholder detection (frontend mirror).
 *
 * Bug #68. The backend (``api/src/integrations/sanmar/placeholder.py``)
 * filters SanMar's ``ImageNotAvailable*.jpg`` placeholder out of the
 * EPDD-derived image URLs at ingest, so new SKU-meta responses never
 * carry it. This module is the defense-in-depth gate at the display
 * path: it suppresses pre-existing stored placeholder URLs flowing
 * through legacy surfaces (cached supplier-meta in the browser, a stale
 * row from a pre-fix ingest, etc.) so the product editor never paints
 * SanMar's placeholder PNG as if it were the real product image.
 *
 * Match rules — kept in lockstep with the backend predicate:
 *   - Filename basename starting with ``ImageNotAvailable`` (case-insensitive)
 *   - Either bare-filename form (``ImageNotAvailable.jpg``) or full URL
 *     (``https://cdnm.sanmar.com/imglib/mresjpg/ImageNotAvailable.jpg``).
 *   - Trailing whitespace tolerated.
 */

// Anchored at a path boundary (``/`` or start-of-string) so unrelated
// strings that merely contain ``imagenotavailable`` as a substring elsewhere
// don't match. The trailing ``$`` anchors at end of string (modulo the
// `.trim()` in the caller).
const PLACEHOLDER_RE =
    /(^|\/)imagenotavailable[a-z0-9_-]*\.[a-z]{3,4}$/i

/**
 * Return true iff ``url`` is SanMar's "no image" placeholder.
 *
 * Accepts bare filenames or full URLs. Returns false for null, undefined,
 * empty string, and any non-string input.
 *
 * @param {string|null|undefined} url
 * @returns {boolean}
 */
export function isSanmarPlaceholderUrl(url) {
    if (typeof url !== 'string') return false
    const stripped = url.trim()
    if (!stripped) return false
    return PLACEHOLDER_RE.test(stripped)
}
